import * as Sentry from '@sentry/vue';
import { ref } from 'vue';
import type { Ref } from 'vue';
import type {
  CommitmentResponse,
  EventResponse,
  PaymentMethodResponse,
} from '@@/types/api';
import { ApiError } from '~/composables/api/api-error';
import { useCommitmentsApi } from '~/composables/api/use-commitments-api';
import { useStripeApi } from '~/composables/api/use-stripe-api';
import { useMetaPixel } from '~/composables/use-meta-pixel';
import { buildMetaConversionContext } from '~/utils/meta-conversion-context';
import type { MetaConversionContext } from '~/utils/meta-conversion-context';
import { runPostSuccess } from '~/utils/run-post-success';

interface UseCommitmentActionsOptions {
  eventId: Ref<string>;
  event: Ref<EventResponse | null>;
  commitment: Ref<CommitmentResponse | null>;
  enrich: () => Promise<void>;
  refreshPublicEvent: () => Promise<void>;
}

export function useCommitmentActions(options: UseCommitmentActionsOptions) {
  const { eventId, event, commitment, enrich, refreshPublicEvent } = options;
  const commitmentsApi = useCommitmentsApi();
  const stripeApi = useStripeApi();
  const { trackEvent } = useMatomo();
  const { trackInitiateCheckout, trackPurchase } = useMetaPixel();

  // Dialog visibility
  const showCommitDialog = ref(false);
  const showWithdrawDialog = ref(false);
  const showUpdatePaymentDialog = ref(false);

  // Loading states
  const isCommitting = ref(false);
  const isWithdrawing = ref(false);
  const isUpdatingPayment = ref(false);

  // Payment methods
  const paymentMethods = ref<PaymentMethodResponse[]>([]);
  const isLoadingPaymentMethods = ref(false);

  // Idempotency key for commit — preserved across retries
  const commitIdempotencyKey = ref('');

  // Snackbar state
  const snackbar = ref(false);
  const snackbarText = ref('');
  const snackbarColor = ref('success');

  function showSnackbar(text: string, color = 'success') {
    snackbarText.value = text;
    snackbarColor.value = color;
    snackbar.value = true;
  }

  // --- Payment methods ---

  async function fetchPaymentMethods() {
    isLoadingPaymentMethods.value = true;
    try {
      const response = await stripeApi.listPaymentMethods();
      paymentMethods.value = response.data;
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      isLoadingPaymentMethods.value = false;
    }
  }

  function handleCardAdded() {
    fetchPaymentMethods();
  }

  // --- Commit flow ---

  function openCommitDialog() {
    commitIdempotencyKey.value = crypto.randomUUID();
    showCommitDialog.value = true;
    fetchPaymentMethods();
    trackEvent('Commitment', 'DialogOpened', eventId.value);
    trackInitiateCheckout({
      eventId: eventId.value,
      valueCents: event.value?.pricing.commit.total ?? null,
      currency: event.value?.currency ?? 'USD',
      eventID: crypto.randomUUID(),
      contentType: 'commit',
    });
  }

  async function handleCommitSubmit(
    paymentMethodId: string,
    registrationTypeId: string,
    partySize: number = 1,
  ) {
    isCommitting.value = true;

    // Built BEFORE the commit's `try` on purpose. If this ran inside it and
    // ever threw, that catch would report to Sentry tagged
    // `operation: 'commit'` and tell the user "Failed to commit." for a commit
    // that actually succeeded. `buildMetaConversionContext` is contracted never
    // to throw, and this placement makes that failure mode structurally
    // impossible rather than merely unlikely.
    //
    // The local catch is defence in depth for the other half of the hazard: a
    // throw here must not stop the commit from being attempted at all. Meta
    // attribution is never worth failing a payment over. Reported as a warning
    // (not an exception) because the payment itself is unaffected.
    let conversionContext: MetaConversionContext = {};
    try {
      conversionContext = buildMetaConversionContext();
    } catch {
      Sentry.captureMessage(
        'buildMetaConversionContext threw — commit sent without Meta click identity',
        {
          level: 'warning',
          tags: { operation: 'commit', event_id: eventId.value },
        },
      );
    }

    // The outer `try`/`finally` owns `isCommitting` and resets it only after
    // the post-success refresh settles, so the loading flag spans the whole
    // action (same convention in every mutation handler here).
    try {
      // Only the API call lives in the inner `try`. Everything after it
      // (tracking, refresh) runs through `runPostSuccess`, because a throw
      // there would otherwise land in that `catch`, be reported to Sentry as
      // `operation: 'commit'`, and tell the user "Failed to commit." for a
      // commit that succeeded and will be charged — exactly what happened when
      // the response was read as `{ data }` but arrived unwrapped (#1980).
      let created: CommitmentResponse;
      try {
        created = await commitmentsApi.commitToEvent(
          eventId.value,
          {
            // Spread FIRST so a future key collision can never overwrite a
            // payment field.
            ...conversionContext,
            payment_method_id: paymentMethodId,
            registration_type_id: registrationTypeId,
            party_size: partySize,
          },
          commitIdempotencyKey.value,
        );
      } catch (e) {
        Sentry.captureException(e, {
          tags: { operation: 'commit', event_id: eventId.value },
        });
        const isNetwork = e instanceof ApiError && e.isNetworkError;
        showSnackbar(
          isNetwork
            ? 'Network error — check your connection and try again.'
            : 'Failed to commit. Please try again.',
          'error',
        );
        return;
      }

      showCommitDialog.value = false;
      showSnackbar(
        "You're committed! Your card will be charged if the event reaches its goal.",
      );

      await runPostSuccess('commit', eventId.value, () => {
        trackEvent('Commitment', 'Created', eventId.value);
        // The response's pricing_summary is the authoritative figure — it is
        // party-size- and registration-type-aware, and is the same breakdown
        // the server-side Purchase uses. The event's advertised pricing.commit
        // is per-ticket and must not be substituted. If it is missing we send
        // no value at all: Meta keeps one half of the dedup pair and drops the
        // other, so a guessed value would silently misreport ROAS.
        if (!created.pricing_summary) {
          Sentry.captureMessage(
            'Commit response missing pricing_summary — Purchase pixel value omitted',
            {
              level: 'warning',
              tags: { operation: 'commit', event_id: eventId.value },
            },
          );
        }
        trackPurchase({
          eventId: eventId.value,
          valueCents: created.pricing_summary?.total ?? null,
          currency: event.value?.currency ?? 'USD',
          eventID: created.id,
          contentType: 'commit',
        });
      });
      await runPostSuccess('commit', eventId.value, () =>
        Promise.all([enrich(), refreshPublicEvent()]),
      );
    } finally {
      isCommitting.value = false;
    }
  }

  // --- Withdraw flow ---

  function openWithdrawDialog() {
    showWithdrawDialog.value = true;
  }

  async function handleWithdrawConfirm() {
    if (!commitment.value) return;
    const commitmentId = commitment.value.id;
    isWithdrawing.value = true;
    try {
      // Only the API call is inside the reporting `try` — a tracking or
      // refresh failure after a successful withdraw must not tell the user
      // the withdraw failed (they would retry against a withdrawn commitment).
      // See `handleCommitSubmit` and #1980.
      try {
        await commitmentsApi.withdrawCommitment(eventId.value, commitmentId);
      } catch (e) {
        Sentry.captureException(e, {
          tags: { operation: 'withdraw', event_id: eventId.value },
        });
        const isNetwork = e instanceof ApiError && e.isNetworkError;
        showSnackbar(
          isNetwork
            ? 'Network error — check your connection and try again.'
            : 'Failed to withdraw. Please try again.',
          'error',
        );
        return;
      }

      showWithdrawDialog.value = false;
      showSnackbar('Your commitment has been withdrawn.');

      await runPostSuccess('withdraw', eventId.value, () =>
        trackEvent('Commitment', 'Withdrawn', eventId.value),
      );
      await runPostSuccess('withdraw', eventId.value, () =>
        Promise.all([enrich(), refreshPublicEvent()]),
      );
    } finally {
      isWithdrawing.value = false;
    }
  }

  // --- Update payment method flow ---

  function openUpdatePaymentDialog() {
    showUpdatePaymentDialog.value = true;
    fetchPaymentMethods();
  }

  async function handleUpdatePaymentSubmit(paymentMethodId: string) {
    if (!commitment.value) return;
    const wasDeclined = commitment.value.payment_status === 'awaiting_retry';
    const commitmentId = commitment.value.id;
    isUpdatingPayment.value = true;
    try {
      // Only the API call is inside the reporting `try`. A declined backer
      // told "Failed to update payment method." after the swap actually
      // succeeded (and a retry charge was queued) would keep swapping cards.
      // See `handleCommitSubmit` and #1980.
      try {
        await commitmentsApi.updatePaymentMethod(commitmentId, {
          payment_method_id: paymentMethodId,
        });
      } catch (e) {
        Sentry.captureException(e, {
          tags: {
            operation: 'update_payment_method',
            event_id: eventId.value,
          },
        });
        const isNetwork = e instanceof ApiError && e.isNetworkError;
        showSnackbar(
          isNetwork
            ? 'Network error — check your connection and try again.'
            : 'Failed to update payment method. Please try again.',
          'error',
        );
        return;
      }

      showUpdatePaymentDialog.value = false;
      showSnackbar(
        wasDeclined
          ? 'Payment method updated — retrying charge now.'
          : 'Payment method updated.',
      );

      await runPostSuccess('update_payment_method', eventId.value, () =>
        trackEvent('Commitment', 'PaymentUpdated', eventId.value),
      );
      // The response is only the new card details, not the commitment, so
      // the refreshed commitment state comes from `enrich()`. The public
      // event is unaffected by a card swap.
      await runPostSuccess('update_payment_method', eventId.value, () =>
        enrich(),
      );
    } finally {
      isUpdatingPayment.value = false;
    }
  }

  return {
    // Dialog visibility
    showCommitDialog,
    showWithdrawDialog,
    showUpdatePaymentDialog,
    // Loading states
    isCommitting,
    isWithdrawing,
    isUpdatingPayment,
    // Payment methods
    paymentMethods,
    isLoadingPaymentMethods,
    handleCardAdded,
    // Idempotency
    commitIdempotencyKey,
    // Snackbar
    snackbar,
    snackbarText,
    snackbarColor,
    // Handlers
    openCommitDialog,
    handleCommitSubmit,
    openWithdrawDialog,
    handleWithdrawConfirm,
    openUpdatePaymentDialog,
    handleUpdatePaymentSubmit,
  };
}
